import type { PipelineOutcome } from "./pipeline";
import type { Symbology } from "./formats";

/**
 * Main-thread client for the decode worker. Every failure path resolves to
 * null so the scanner can fall back to inline decoding — the worker is an
 * optimization, never a requirement.
 */
export class WorkerDecoder {
  private seq = 0;
  private pending = new Map<
    number,
    { resolve: (o: PipelineOutcome | null) => void }
  >();

  private constructor(private readonly worker: Worker) {}

  static async create(
    factory: () => Worker,
    wasmUrl: string | undefined,
    timeoutMs = 8000,
  ): Promise<WorkerDecoder | null> {
    let worker: Worker;
    try {
      worker = factory();
    } catch {
      return null;
    }
    const client = new WorkerDecoder(worker);

    const ready = await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), timeoutMs);
      worker.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };
      worker.onmessage = (e: MessageEvent) => {
        if (e.data?.type === "ready") {
          clearTimeout(timer);
          resolve(true);
        }
      };
      try {
        worker.postMessage({ id: 0, type: "init", wasmUrl });
      } catch {
        clearTimeout(timer);
        resolve(false);
      }
    });

    if (!ready) {
      worker.terminate();
      return null;
    }
    worker.onmessage = (e: MessageEvent) => client.onMessage(e);
    worker.onerror = () => client.failAll();
    return client;
  }

  private onMessage(e: MessageEvent): void {
    const { id, type } = e.data ?? {};
    const entry = this.pending.get(id);
    if (!entry) return;
    this.pending.delete(id);
    if (type === "result") {
      const { results, sawRegions, luma, skippedBlurry } = e.data;
      entry.resolve({ results, sawRegions, luma, skippedBlurry });
    } else {
      entry.resolve(null);
    }
  }

  private failAll(): void {
    for (const [, entry] of this.pending) entry.resolve(null);
    this.pending.clear();
  }

  /** Transfers the frame buffer (zero copy). Null = worker failed this frame. */
  run(
    image: ImageData,
    symbologies: Symbology[],
    thorough: boolean,
  ): Promise<PipelineOutcome | null> {
    const id = ++this.seq;
    return new Promise((resolve) => {
      this.pending.set(id, { resolve });
      try {
        this.worker.postMessage(
          {
            id,
            type: "frame",
            buffer: image.data.buffer,
            width: image.width,
            height: image.height,
            symbologies,
            thorough,
          },
          [image.data.buffer],
        );
      } catch {
        this.pending.delete(id);
        resolve(null);
      }
    });
  }

  terminate(): void {
    this.failAll();
    this.worker.terminate();
  }
}
